import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button, Card, Icon, Typography } from 'components';

import type { Category, Metric } from '@repo/types';

import THCheckbox from '../../../../components/THCheckbox/THCheckbox';

import styles from './CategoryMetricsCheckboxFilter.module.scss';
import c from 'classnames';
import { getEvaluationIconFromColor } from '../EvaluationGroupsSummary/EvaluationGroupsSummary';
import { Tooltip } from '@repo/ui';
import useKeyInsightsSections from '../../../../services/insights/useKeyInsightsSections';

export interface CategoryMetricsCheckboxFilterProps {
  healthInsightsFilters: string[];
  categoryFilters: string[];
  groupedSubmetrics: Category[];
  healthInsightsCounts: {
    great_results: number;
    okay_results: number;
    needs_improvement: number;
    needs_support: number;
  };
  onFiltersChange: (healthInsights: string[], categories: string[]) => void;
}

const SingleMetric = ({
  count,
  color,
  styleClass,
}: {
  count: number;
  color: string;
  styleClass: string;
}) => {
  if (count > 0) {
    return (
      <div className='flex gap-1'>
        <Icon
          name={getEvaluationIconFromColor(color)}
          size='s'
          className={styleClass}
        />
        <Typography variant='body-s' className='my-auto'>
          {count}
        </Typography>
      </div>
    );
  }
  return null;
};

export const HEALTH_INSIGHTS_OPTIONS = [
  {
    value: 'needs_support',
    label: 'Needs Support',
    iconName: 'alertTriangleFilled',
    color: 'red',
  },
  {
    value: 'needs_improvement',
    label: 'Needs Improvement',
    iconName: 'alertCircleFilled',
    color: 'orange',
  },
  {
    value: 'okay_results',
    label: 'Okay Results',
    iconName: 'checkFilled',
    color: 'grey',
  },
  {
    value: 'great_results',
    label: 'Great Results',
    iconName: 'checkVerifiedFilled',
    color: 'green',
  },
];

const evaluationColorMap: Record<string, string> = {
  red: 'needs_support',
  orange: 'needs_improvement',
  grey: 'okay_results',
  green: 'great_results',
};

const CategoryMetricsCheckboxFilter: React.FC<
  CategoryMetricsCheckboxFilterProps
> = ({
  healthInsightsFilters,
  categoryFilters,
  groupedSubmetrics,
  healthInsightsCounts,
  onFiltersChange,
}) => {
  const { data: keyInsightsSections } = useKeyInsightsSections();

  const tooltipTexts = useMemo(() => {
    if (!keyInsightsSections) return {};
    return keyInsightsSections.reduce(
      (acc, section) => {
        acc[section.display_title] = section.short_description || '';
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [keyInsightsSections]);

  const allHealthInsights = HEALTH_INSIGHTS_OPTIONS.map(o => o.value);
  const allCategories = groupedSubmetrics.map(g => g.name);

  // Clean filter handlers
  const handleHealthInsightToggle = useCallback(
    (value: string) => {
      const isAllSelected =
        healthInsightsFilters.length === allHealthInsights.length;
      const isCurrentlySelected = healthInsightsFilters.includes(value);

      let newHealthInsights: string[];
      if (isAllSelected && isCurrentlySelected) {
        // If all are selected and user clicks one, select only that one
        newHealthInsights = [value];
      } else if (isCurrentlySelected) {
        // If currently selected, remove it
        newHealthInsights = healthInsightsFilters.filter(
          item => item !== value,
        );
      } else {
        // If not selected, add it
        newHealthInsights = [...healthInsightsFilters, value];
      }

      onFiltersChange(newHealthInsights, categoryFilters);
    },
    [
      healthInsightsFilters,
      categoryFilters,
      allHealthInsights.length,
      onFiltersChange,
    ],
  );

  const handleCategoryToggle = useCallback(
    (value: string) => {
      const isAllSelected = categoryFilters.length === allCategories.length;
      const isCurrentlySelected = categoryFilters.includes(value);

      let newCategories: string[];
      if (isAllSelected && isCurrentlySelected) {
        // If all are selected and user clicks one, select only that one
        newCategories = [value];
      } else if (isCurrentlySelected) {
        // If currently selected, remove it
        newCategories = categoryFilters.filter(item => item !== value);
      } else {
        // If not selected, add it
        newCategories = [...categoryFilters, value];
      }

      onFiltersChange(healthInsightsFilters, newCategories);
    },
    [
      categoryFilters,
      healthInsightsFilters,
      allCategories.length,
      onFiltersChange,
    ],
  );

  const handleHealthInsightsSelectAll = useCallback(() => {
    // Select all health insights, keep categories as-is
    onFiltersChange(allHealthInsights, categoryFilters);
  }, [allHealthInsights, categoryFilters, onFiltersChange]);

  const handleCategoriesSelectAll = useCallback(() => {
    // Select all categories, keep health insights as-is
    onFiltersChange(healthInsightsFilters, allCategories);
  }, [healthInsightsFilters, allCategories, onFiltersChange]);

  const handleHealthInsightsClearAll = useCallback(() => {
    // Clear only health insights, keep categories
    onFiltersChange([], categoryFilters);
  }, [categoryFilters, onFiltersChange]);

  const handleCategoriesClearAll = useCallback(() => {
    // Clear only categories, keep health insights
    onFiltersChange(healthInsightsFilters, []);
  }, [healthInsightsFilters, onFiltersChange]);

  const getCategoryCounts = (metrics: Metric[]) => {
    const counts = {
      great_results: 0,
      okay_results: 0,
      needs_improvement: 0,
      needs_support: 0,
    };

    metrics.forEach(metric => {
      metric.orderedSubmetrics.forEach(submetric => {
        const evaluationName =
          evaluationColorMap[submetric.evaluation_color ?? ''];
        if (
          evaluationName &&
          (healthInsightsFilters.length === 0 ||
            healthInsightsFilters.includes(evaluationName))
        ) {
          counts[evaluationName as keyof typeof counts]++;
        }
      });
    });

    return counts;
  };

  return (
    <>
      <Card className={c(styles.container, 'hide-on-mobile')}>
        <div className={styles.desktopContainer}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Typography
                variant='label'
                weight='medium'
                className={styles.sectionTitle}
              >
                Health Insights
              </Typography>
              <div className={styles.actionButtons}>
                <Button
                  color='purple'
                  variant='text'
                  size='small'
                  onClick={handleHealthInsightsSelectAll}
                >
                  Select all
                </Button>
                <Button
                  color='purple'
                  variant='text'
                  size='small'
                  onClick={handleHealthInsightsClearAll}
                >
                  Clear all
                </Button>
              </div>
            </div>
            <div className={styles.filters}>
              {HEALTH_INSIGHTS_OPTIONS.map(
                ({ value, label, iconName, color }) => (
                  <div key={value} className={styles.filterRow}>
                    <THCheckbox
                      label={
                        <div className={c('flex', styles.labelContainer)}>
                          <div className={c('flex', styles.categoryInfo)}>
                            <Icon
                              name={iconName}
                              size='s'
                              className={styles[color]}
                            />
                            <Typography
                              variant='label'
                              className={styles.label}
                            >
                              {label}{' '}
                              {healthInsightsCounts &&
                                `(${
                                  healthInsightsCounts[
                                    value as keyof typeof healthInsightsCounts
                                  ]
                                })`}
                            </Typography>
                          </div>
                        </div>
                      }
                      name='health-insight'
                      value={value}
                      defaultChecked={healthInsightsFilters.includes(value)}
                      onChange={async () => {
                        handleHealthInsightToggle(value);
                      }}
                      onBlur={async () => {}}
                    />
                  </div>
                ),
              )}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <Typography
                variant='label'
                weight='medium'
                className={styles.sectionTitle}
              >
                Categories
              </Typography>
              <div className={styles.actionButtons}>
                <Button
                  color='purple'
                  variant='text'
                  size='small'
                  onClick={handleCategoriesSelectAll}
                >
                  Select all
                </Button>
                <Button
                  color='purple'
                  variant='text'
                  size='small'
                  onClick={handleCategoriesClearAll}
                >
                  Clear all
                </Button>
              </div>
            </div>
            <div className={styles.filters}>
              {groupedSubmetrics.map(({ name, orderedMetrics }) => {
                const categoryCounts = getCategoryCounts(orderedMetrics);
                return (
                  <div key={name} className={styles.filterRow}>
                    <THCheckbox
                      label={
                        <div className={c('flex', styles.labelContainer)}>
                          <div className='flex gap-2'>
                            <Typography
                              variant='label'
                              className={styles.label}
                            >
                              {name}
                            </Typography>
                            <Tooltip
                              content={tooltipTexts[name]}
                              trigger='hover'
                              position='top'
                            >
                              <span
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                <Icon
                                  name='infoCircle'
                                  size='s'
                                  className={styles.infoIcon}
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <div className={styles.categoryIcons}>
                            {categoryCounts.needs_support > 0 && (
                              <SingleMetric
                                count={categoryCounts.needs_support}
                                color='red'
                                styleClass={styles.red}
                              />
                            )}
                            {categoryCounts.needs_improvement > 0 && (
                              <SingleMetric
                                count={categoryCounts.needs_improvement}
                                color='orange'
                                styleClass={styles.orange}
                              />
                            )}
                            {categoryCounts.okay_results > 0 && (
                              <SingleMetric
                                count={categoryCounts.okay_results}
                                color='grey'
                                styleClass={styles.grey}
                              />
                            )}
                            {categoryCounts.great_results > 0 && (
                              <SingleMetric
                                count={categoryCounts.great_results}
                                color='green'
                                styleClass={styles.green}
                              />
                            )}
                          </div>
                        </div>
                      }
                      name='category'
                      value={name}
                      defaultChecked={categoryFilters.includes(name)}
                      onChange={async () => {
                        handleCategoryToggle(name);
                      }}
                      onBlur={async () => {}}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
      <div className={c(styles.mobileContainer, 'hide-on-desktop')}>
        <div className={styles.healthInsightsDropdown}>
          <div className={styles.mobileFilterHeader}>
            <Typography variant='label'>Filter by evaluation</Typography>
            <div className={styles.mobileActionButtons}>
              <Button
                color='purple'
                variant='text'
                size='small'
                onClick={handleHealthInsightsSelectAll}
              >
                Select all
              </Button>
              <Button
                color='purple'
                variant='text'
                size='small'
                onClick={handleHealthInsightsClearAll}
              >
                Clear all
              </Button>
            </div>
          </div>
          <details className={styles.mobileSection}>
            <summary className={styles.summary}>
              <Typography variant='label' weight='medium'>
                {healthInsightsFilters.length === 0
                  ? 'None'
                  : healthInsightsFilters.length === allHealthInsights.length
                    ? 'All'
                    : healthInsightsFilters
                        .map(
                          filterValue =>
                            HEALTH_INSIGHTS_OPTIONS.find(
                              option => option.value === filterValue,
                            )?.label || filterValue,
                        )
                        .join(', ')}
              </Typography>
              <Icon name='chevronDown' size='s' className={styles.chevron} />
            </summary>

            <div className={styles.filters}>
              {HEALTH_INSIGHTS_OPTIONS.map(
                ({ value, label, iconName, color }) => (
                  <div key={value} className={styles.filterRow}>
                    <THCheckbox
                      label={
                        <div className={c('flex', styles.categoryInfo)}>
                          <Icon
                            name={iconName as any}
                            size='s'
                            className={styles[color]}
                          />
                          <Typography variant='label' className={styles.label}>
                            {label}{' '}
                            {healthInsightsCounts &&
                              `(${
                                healthInsightsCounts[
                                  value as keyof typeof healthInsightsCounts
                                ]
                              })`}
                          </Typography>
                        </div>
                      }
                      name='health-insight-mobile'
                      value={value}
                      defaultChecked={healthInsightsFilters.includes(value)}
                      onChange={async () => {
                        handleHealthInsightToggle(value);
                      }}
                      onBlur={async () => {}}
                    />
                  </div>
                ),
              )}
            </div>
          </details>
        </div>

        <div className={styles.categoryDropdown}>
          <div className={styles.mobileFilterHeader}>
            <Typography variant='label'>Filter by category</Typography>
            <div className={styles.mobileActionButtons}>
              <Button
                color='purple'
                variant='text'
                size='small'
                onClick={handleCategoriesSelectAll}
              >
                Select all
              </Button>
              <Button
                color='purple'
                variant='text'
                size='small'
                onClick={handleCategoriesClearAll}
              >
                Clear all
              </Button>
            </div>
          </div>
          <details className={styles.mobileSection}>
            <summary className={styles.summary}>
              <Typography variant='label' weight='medium'>
                {categoryFilters.length === 0
                  ? 'None'
                  : categoryFilters.length === allCategories.length
                    ? 'All'
                    : categoryFilters.join(', ')}
              </Typography>
              <Icon name='chevronDown' size='s' className={styles.chevron} />
            </summary>
            <div className={styles.filters}>
              {groupedSubmetrics.map(({ name, orderedMetrics }) => {
                const categoryCounts = getCategoryCounts(orderedMetrics);
                return (
                  <div key={name} className={styles.filterRow}>
                    <THCheckbox
                      label={
                        <div className={c('flex', styles.labelContainer)}>
                          <div className='flex gap-2'>
                            <Typography
                              variant='label'
                              className={styles.label}
                            >
                              {name}
                            </Typography>
                            <Tooltip
                              content={tooltipTexts[name]}
                              trigger='hover'
                              position='top'
                            >
                              <span
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                }}
                              >
                                <Icon
                                  name='infoCircle'
                                  size='s'
                                  className={styles.infoIcon}
                                />
                              </span>
                            </Tooltip>
                          </div>
                          <div className={styles.categoryIcons}>
                            {categoryCounts.needs_support > 0 && (
                              <SingleMetric
                                count={categoryCounts.needs_support}
                                color='red'
                                styleClass={styles.red}
                              />
                            )}
                            {categoryCounts.needs_improvement > 0 && (
                              <SingleMetric
                                count={categoryCounts.needs_improvement}
                                color='orange'
                                styleClass={styles.orange}
                              />
                            )}
                            {categoryCounts.okay_results > 0 && (
                              <SingleMetric
                                count={categoryCounts.okay_results}
                                color='grey'
                                styleClass={styles.grey}
                              />
                            )}
                            {categoryCounts.great_results > 0 && (
                              <SingleMetric
                                count={categoryCounts.great_results}
                                color='green'
                                styleClass={styles.green}
                              />
                            )}
                          </div>
                        </div>
                      }
                      name='category-mobile'
                      value={name}
                      defaultChecked={categoryFilters.includes(name)}
                      onChange={async () => handleCategoryToggle(name)}
                      onBlur={async () => {}}
                    />
                  </div>
                );
              })}
            </div>
          </details>
        </div>
      </div>
    </>
  );
};

export default CategoryMetricsCheckboxFilter;
